import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const PROVIDERS_QUERY = graphql(`
  query SocialLoginProviders {
    socialLoginProviders
  }
`);

// The page's baked-in token, used only as a last-resort fallback if the fresh
// fetch below fails (no worse than the old behavior).
function metaToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

// Fetch a CSRF token minted against the CURRENT session right before submitting,
// then submit the native form. The token baked into the page's <meta> tag drifts
// out of sync with the session cookie when /signin is reached via client-side
// navigation (or after sign-out); the OmniAuth POST is the one app POST that
// hard-fails on a stale token (sending the user to /signin?error, succeeding only
// on retry). Fetching fresh here removes that race. See CsrfTokensController.
export async function submitWithFreshToken(form: HTMLFormElement) {
  try {
    const res = await fetch("/auth/csrf", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (res.ok) {
      const { token } = await res.json();
      const input = form.querySelector<HTMLInputElement>('input[name="authenticity_token"]');
      if (input && token) input.value = token;
    }
  } catch {
    // Network hiccup: fall back to the existing (meta) token — same as before.
  }
  form.submit();
}

// `role` is the role the visitor chose on the signup form. When "tutor", it's
// forwarded through the OAuth request phase so a brand-new account is created as
// a tutor (and routed to tutor onboarding). Omitted on sign-in (existing users
// keep their role regardless).
// On the sign-up page, pass requireConsent + consentGiven so the OAuth buttons
// forward the click-wrap consent (18+ + Terms/Privacy, #170). While the box is
// unticked the buttons stay enabled but a click calls onConsentMissing instead
// of submitting, so the page can point at the box (#993) — a disabled button
// can't explain itself, and hover doesn't exist on touch. On sign-in all three
// are omitted — existing users only.
// `marketingOptIn` is the sign-up page's second, optional box: a yes to news
// and offers by email (#722). Forwarded only when ticked, and honoured by the
// server only when the callback creates a brand-new account.
export default function SocialLoginButtons({ role, requireConsent = false, consentGiven = false, marketingOptIn = false, referralCode, onConsentMissing }: { role?: string; requireConsent?: boolean; consentGiven?: boolean; marketingOptIn?: boolean; referralCode?: string; onConsentMissing?: () => void }) {
  const { t, i18n } = useTranslation();

  function handleOAuthSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // On /signup these forms render nested inside the page's own <form>
    // (invalid DOM the browser tolerates); without this the submit bubbles up
    // and fires the email-signup mutation with empty fields alongside the
    // OAuth redirect.
    e.stopPropagation();
    // The server refuses a consent-less signup regardless (#170/#960); this
    // only decides what the click does for the person in front of the form.
    if (requireConsent && !consentGiven) {
      onConsentMissing?.();
      return;
    }
    // Capture the form before awaiting — React nulls currentTarget after the handler.
    void submitWithFreshToken(e.currentTarget);
  }
  const { data, isPending } = useQuery({
    queryKey: ["socialLoginProviders"],
    queryFn: async () => {
      const r = await graphqlClient.request(PROVIDERS_QUERY);
      return r.socialLoginProviders;
    },
    staleTime: 60 * 60 * 1000,
  });

  const providers = data ?? [];
  // While the query is in flight, reserve space with a disabled placeholder so the
  // page doesn't flash "no SSO" then pop a button in. Once resolved: render the
  // configured providers, or nothing if none are configured.
  if (isPending) {
    return (
      <>
        <div className="space-y-3">
          <Button type="button" variant="outline" disabled className="w-full h-11 animate-pulse" aria-hidden />
        </div>
        <div className="relative my-2">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">{t("auth.social.divider")}</span>
        </div>
      </>
    );
  }
  if (providers.length === 0) return null;

  // Forward the chosen role only for tutors (students are the default
  // server-side) and any `?ref=CODE` referral, threaded through the OAuth
  // request phase (OmniAuth surfaces both in the callback's omniauth.params).
  const action = (provider: string) => {
    const params = new URLSearchParams();
    if (role === "tutor") params.set("intent", "tutor");
    if (referralCode) params.set("ref", referralCode);
    // Base language code ("pt-BR" → "pt"): applied server-side only when the
    // callback creates a brand-new account (sets ui_locale from birth).
    const locale = i18n.language?.split("-")[0];
    if (locale) params.set("locale", locale);
    // The click-wrap consent (18+ + Terms/Privacy, #170) MUST ride the query
    // string like the params above: OmniAuth carries only request.GET across
    // the provider redirect (session["omniauth.params"]), so a form-body
    // field never reaches the callback and a new signup is refused (#960).
    if (requireConsent && consentGiven) params.set("accepted_legal", "1");
    // Same road for the optional news-and-offers box (#722). No param at all
    // is the "no": the server takes only "1" (or "true") for a yes.
    if (marketingOptIn) params.set("marketing_opt_in", "1");
    const qs = params.toString();
    return qs ? `/auth/${provider}?${qs}` : `/auth/${provider}`;
  };

  return (
    <>
      <div className="space-y-3">
        {providers.includes("google_oauth2") && (
          <form method="post" action={action("google_oauth2")} onSubmit={handleOAuthSubmit}>
            <input type="hidden" name="authenticity_token" defaultValue={metaToken()} />
            <Button type="submit" variant="outline" className="w-full h-11 gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {t("auth.social.continueWithGoogle")}
            </Button>
          </form>
        )}
        {providers.includes("apple") && (
          <form method="post" action={action("apple")} onSubmit={handleOAuthSubmit}>
            <input type="hidden" name="authenticity_token" defaultValue={metaToken()} />
            <Button type="submit" className="w-full h-11 gap-3 bg-foreground text-background hover:bg-foreground/90">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {t("auth.social.continueWithApple")}
            </Button>
          </form>
        )}
      </div>

      <div className="relative my-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">{t("auth.social.divider")}</span>
      </div>
    </>
  );
}
